import { Show, createMemo, type Accessor } from 'solid-js';
import {
  adjustDatetimeLocalBySeconds,
  taggerEventTypeIsPrestart,
  taggerEventTypeIsRace,
  taggerEventTypeUsesFocusTime,
} from '@utils/taggerEventEditForm';

export default function TagsExploreEditTimeFields(props: {
  eventTypeUpper: Accessor<string>;
  focusLocal: Accessor<string>;
  setFocusLocal: (v: string) => void;
  startLocal: Accessor<string>;
  setStartLocal: (v: string) => void;
  endLocal: Accessor<string>;
  setEndLocal: (v: string) => void;
  raceNumberLocal: Accessor<string>;
  setRaceNumberLocal: (v: string) => void;
  /** Race and prestart: one start-time picker, in place of the note. */
  startOnly?: Accessor<boolean>;
  idPrefix?: string;
}) {
  const idPrefix = () => props.idPrefix ?? 'tags-explore-edit';
  const startOnly = () => props.startOnly?.() === true;
  const showCommentFocus = createMemo(() => !startOnly() && taggerEventTypeUsesFocusTime(props.eventTypeUpper()));
  const showPrestartFocus = createMemo(() => !startOnly() && taggerEventTypeIsPrestart(props.eventTypeUpper()));
  const showRange = createMemo(() => {
    const u = props.eventTypeUpper();
    return u === 'TEST' || u === 'SELECTION' || u === 'CREW' || u === 'SAILS' || u === 'CONFIG' || u === 'RACE';
  });
  const showStartField = createMemo(() => startOnly() || showRange());
  const showEndField = createMemo(() => !startOnly() && showRange());
  const showPrestartNumber = createMemo(() => !startOnly() && taggerEventTypeIsPrestart(props.eventTypeUpper()));
  const showRaceNumber = createMemo(() => !startOnly() && taggerEventTypeIsRace(props.eventTypeUpper()));

  return (
    <div class="tags-explore-edit-modal__time-fields">
      <Show when={showPrestartNumber()}>
        <div class="tagger-field tagger-field--full">
          <label for={`${idPrefix()}-prestart-number`}>Prestart number</label>
          <input
            id={`${idPrefix()}-prestart-number`}
            type="number"
            min={1}
            step={1}
            inputmode="numeric"
            class="tagger-datetime-input tagger-race-number-input"
            value={props.raceNumberLocal()}
            onInput={(e) => props.setRaceNumberLocal(e.currentTarget.value)}
          />
        </div>
      </Show>
      <Show when={showRaceNumber()}>
        <div class="tagger-field tagger-field--full">
          <label for={`${idPrefix()}-race-number`}>Race number</label>
          <input
            id={`${idPrefix()}-race-number`}
            type="number"
            min={1}
            step={1}
            inputmode="numeric"
            class="tagger-datetime-input tagger-race-number-input"
            value={props.raceNumberLocal()}
            onInput={(e) => props.setRaceNumberLocal(e.currentTarget.value)}
          />
          <span class="tagger-field__hint">Optional. Used for processing when set.</span>
        </div>
      </Show>
      <Show when={showCommentFocus()}>
        <div class="tagger-field tagger-field--full">
          <label for={`${idPrefix()}-focus-time`}>
            Focus time{' '}
            <span class="tagger-focus-time-label-note">(offset by -5 seconds by default)</span>
          </label>
          <div class="tagger-focus-time-row">
            <button
              type="button"
              class="tagger-btn tagger-btn--secondary tagger-focus-time-adjust"
              onClick={() => props.setFocusLocal(adjustDatetimeLocalBySeconds(props.focusLocal(), -10))}
            >
              -10
            </button>
            <input
              id={`${idPrefix()}-focus-time`}
              type="datetime-local"
              step={1}
              class="tagger-datetime-input"
              value={props.focusLocal()}
              onInput={(e) => props.setFocusLocal(e.currentTarget.value)}
            />
            <button
              type="button"
              class="tagger-btn tagger-btn--secondary tagger-focus-time-adjust"
              onClick={() => props.setFocusLocal(adjustDatetimeLocalBySeconds(props.focusLocal(), 10))}
            >
              +10
            </button>
          </div>
        </div>
      </Show>
      <Show when={showPrestartFocus()}>
        <div class="tagger-field tagger-field--full">
          <label for={`${idPrefix()}-prestart-time`}>Focus time</label>
          <input
            id={`${idPrefix()}-prestart-time`}
            type="datetime-local"
            step={1}
            class="tagger-datetime-input"
            value={props.focusLocal()}
            onInput={(e) => props.setFocusLocal(e.currentTarget.value)}
          />
          <span class="tagger-field__hint">
            Manual prestart focus moment. It can be in the past or future; interval runs from 2 minutes before through 30 seconds after.
          </span>
        </div>
      </Show>
      <Show when={showStartField()}>
        <div class="tagger-field tagger-field--full">
          <label for={`${idPrefix()}-start-time`}>Start time</label>
          <input
            id={`${idPrefix()}-start-time`}
            type="datetime-local"
            step={1}
            class="tagger-datetime-input"
            value={props.startLocal()}
            onInput={(e) => props.setStartLocal(e.currentTarget.value)}
          />
        </div>
      </Show>
      <Show when={showEndField()}>
        <div class="tagger-field tagger-field--full">
          <label for={`${idPrefix()}-end-time`}>End time</label>
          <input
            id={`${idPrefix()}-end-time`}
            type="datetime-local"
            step={1}
            class="tagger-datetime-input"
            value={props.endLocal()}
            onInput={(e) => props.setEndLocal(e.currentTarget.value)}
          />
          <span class="tagger-field__hint">Leave empty while interval is still open.</span>
        </div>
      </Show>
    </div>
  );
}
